import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
} from '@angular/core';

import { first, Observable, Subject, switchMap, takeUntil } from 'rxjs';

import { filterBoolean } from '@recruitee/common';
import { I18nService } from '@recruitee/i18n';
import {
  BoxComponent,
  DialogService,
  IconButtonComponent,
  IconComponent,
  IconName,
} from '@recruitee/user-interface';

import { HttpErrorHandlerService } from '../../common';
import { FieldMappingStoreService } from '../data/src/field-mapping/field-mapping.service';
import { FieldMapping } from '../types';
import { sourceIcon, sourceName } from '../utils';

@Component({
  selector: 'rti-field-mapping-form-field-mapping',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form-field-mapping.component.html',
  styleUrls: ['./field-mapping-form-field-mapping.component.less'],
  imports: [IconButtonComponent, IconComponent, BoxComponent],
})
export class FieldMappingFormFieldMappingComponent {
  @Input() public fieldMapping: FieldMapping;
  @Output() public fieldMappingDeleted: EventEmitter<number> = new EventEmitter();

  public icon: IconName;
  public title: string;

  @Input() private integrationKind: string;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private i18n: I18nService,
    private dialog: DialogService,
    private vcRef: ViewContainerRef,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private fieldMappingStore: FieldMappingStoreService,
  ) {}

  public ngOnInit(): void {
    this.icon = this.getIcon();
    this.title = this.getTitle();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public delete(): void {
    this.confirm$()
      .pipe(
        first(),
        filterBoolean,
        switchMap(() =>
          this.fieldMappingStore.deleteFieldMapping(this.integrationKind, this.fieldMapping.id),
        ),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe({
        next: () => {
          this.fieldMappingDeleted.emit(this.fieldMapping.id);
        },
        error: error => {
          this.httpErrorHandlerService.default(error);
        },
      });
  }

  private getTitle(): string {
    const source = {
      name: this.fieldMapping.data.name,
      type: this.fieldMapping.source,
      metadata: this.fieldMapping.data.metadata,
    };

    return `${this.i18n.translate(
      `rti.field_mapping.source.${this.fieldMapping.source}`,
    )}: ${sourceName(source, this.i18n)}`;
  }

  private confirm$(): Observable<boolean> {
    const title = this.i18n.translate('rti.field_mapping.settings.delete.dialog.title');
    const message = this.i18n.translate('rti.field_mapping.settings.delete.dialog.message');

    return this.dialog
      .error(
        {
          title,
          message,
        },
        this.vcRef,
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()));
  }

  private getIcon(): IconName {
    return sourceIcon(this.fieldMapping.source);
  }
}
